import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import clsx from 'clsx'
import { difference } from 'lodash'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'

import { type CheckboxGroupOption } from '@/components/Checkbox/CheckboxGroup'
import { type Column } from '@/components/Table/Table'
import styles from '@/components/Table/Table.module.scss'
import useSetHiddenColumns from '@/mutations/useSetHiddenColumns'
import type { columnsSchema } from '@/utils/zod'

import type { PaginationSearch } from './usePagination'
import { TABLE_CELL_WIDTH } from '../components/Table/table-cell-width'
import useHiddenColumns from '../queries/useHiddenColumnsList'
import type { RouteApiWithSearch } from '../types/route-api-with-search'

type ColumnsSearch = z.infer<typeof columnsSchema>
export type RouteApiWithColumns = RouteApiWithSearch<ColumnsSearch>
export type RouteApiWithColumnsAndPagination = RouteApiWithSearch<
  ColumnsSearch & PaginationSearch
>

type UseColumnsBaseProps = {
  numberColumnClassName?: string
  hideNumberColumnHeader?: boolean
}

type UseColumnsProps = UseColumnsBaseProps & {
  routeApi: RouteApiWithColumnsAndPagination
}

// requires column and pagination params in routeApi
const useColumns = <TData,>(
  allColumns: Column<TData>[],
  props: UseColumnsProps
) => {
  const { page, pageSize } = props.routeApi.useSearch()
  return useColumnsWithOptionalPagination(allColumns, {
    ...props,
    pagination: { page, pageSize }
  })
}

type UseColumnsWithoutPaginationProps = UseColumnsBaseProps & {
  routeApi: RouteApiWithColumns
  pagination?: PaginationSearch
}

// requires only columns param in routeApi
const useColumnsWithOptionalPagination = <TData,>(
  allColumns: Column<TData>[],
  props: UseColumnsWithoutPaginationProps
) =>
  useColumnsWithoutNumberColumn(
    [
      useNumberColumn({
        pagination: props?.pagination,
        className: props?.numberColumnClassName,
        hideHeader: props?.hideNumberColumnHeader
      }),
      ...allColumns
    ],
    { routeApi: props.routeApi }
  )

type UseColumnsWithoutNumberColumnProps = {
  routeApi: RouteApiWithSearch<ColumnsSearch>
}

const useColumnsWithoutNumberColumn = <TData,>(
  allColumns: Column<TData>[],
  props: UseColumnsWithoutNumberColumnProps
) => {
  // column keys
  const allColumnKeys = allColumns.map(({ key }) => key)
  const hiddenColumnKeys = props.routeApi.useSearch().hiddenColumns ?? []
  const activeColumnKeys = difference(allColumnKeys, hiddenColumnKeys)

  // columns
  const columns = allColumns.filter(({ key }) => activeColumnKeys.includes(key))

  // checkbox options
  const options: CheckboxGroupOption[] = allColumns
    .filter(({ alwaysVisible }) => !alwaysVisible)
    .map(({ key, label }) => ({
      value: key,
      label: label ?? ''
    }))

  // sync url with saved preferences from backend
  const navigate = props.routeApi.useNavigate()
  const { data: hiddenColumnsList, refetch: refetchHiddenColumns } =
    useHiddenColumns()
  const hiddenColumnsForThisTable = hiddenColumnsList?.find(
    ({ tableKey }) => tableKey === props.routeApi.id
  )
  useEffect(() => {
    if (!hiddenColumnsForThisTable) return
    navigate({
      search: prev => ({
        ...prev,
        hiddenColumns: hiddenColumnsForThisTable.hiddenColumns
      })
    })
  }, [navigate, hiddenColumnsForThisTable])

  // update url and save preferences to backend
  const { mutate: setHiddenColumns } = useSetHiddenColumns({
    onSuccess: refetchHiddenColumns
  })
  const onChangeActiveColumnKeys = (activeKeys: string[]) => {
    const hiddenColumns = difference(allColumnKeys, activeKeys)
    navigate({
      search: prev => ({
        ...prev,
        hiddenColumns
      })
    })
    setHiddenColumns({ tableKey: props.routeApi.id, hiddenColumns })
  }

  return {
    columns,
    activeColumnKeys,
    options,
    onChangeActiveColumnKeys
  }
}

type UseNumberColumnProps = {
  pagination?: PaginationSearch
  hideHeader?: boolean
  className?: string
}

const useNumberColumn = (props?: UseNumberColumnProps) => {
  const { t } = useTranslation('common')

  return {
    key: 'id',
    title: props?.hideHeader ? (
      <VisuallyHidden.Root>{t('header.number')}</VisuallyHidden.Root>
    ) : (
      t('header.number')
    ),
    label: t('header.number'),
    render: (_, __, index) =>
      (props?.pagination?.pageSize ?? 0) *
        ((props?.pagination?.page ?? 0) - 1) +
      index +
      1,
    width: TABLE_CELL_WIDTH.NUMERATION,
    className: clsx(styles.numberColumn, props?.className)
  } satisfies Column<unknown>
}

export { useColumns, useNumberColumn, useColumnsWithOptionalPagination }
